/** @file Horizontal scene-strip below the field: thumbnails, +/-, reorder, duration. */
import { appStore } from "../core/state.js";
import { execute } from "../core/history.js";
import {
  addSceneCommand,
  deleteSceneCommand,
  reorderSceneCommand,
  setActiveSceneCommand,
  updateSceneDurationCommand,
} from "../core/scene-commands.js";
import { h, clear } from "./dom.js";
import { renderSceneThumbnail } from "./timeline-thumbnail.js";

/** @typedef {import("../core/state.js").AppState} AppState */

const THUMB_W = 96;
const THUMB_H = 48;

/**
 * @param {HTMLElement} container
 * @param {{ store?: typeof appStore }} [deps]
 * @returns {() => void}  unmount fn
 */
export function mountTimeline(container, deps = {}) {
  const store = deps.store ?? appStore;

  /** drag state */
  let draggingIdx = /** @type {number | null} */ (null);

  function render() {
    const state = store.get();
    clear(container);

    const strip = h(
      "div",
      {
        class:
          "timeline-strip flex items-center gap-2 overflow-x-auto px-2 py-2 bg-neutral-800 border-t border-neutral-700",
      },
      [],
    );
    strip.style.touchAction = "pan-x";

    state.scenes.forEach((scene, idx) => {
      const isActive = idx === state.activeSceneIndex;
      const thumbUrl = renderSceneThumbnail(scene, {
        width: THUMB_W,
        height: THUMB_H,
        tokens: state.tokens,
      });

      const card = h(
        "button",
        {
          type: "button",
          class: [
            "scene-card relative flex flex-col items-stretch gap-1 p-1 rounded border-2",
            isActive ? "border-amber-400 scene-card--active" : "border-neutral-600",
            "min-h-[80px] min-w-[100px] bg-neutral-900 shrink-0",
          ].join(" "),
          "data-scene-idx": String(idx),
        },
        [
          h("img", {
            src: thumbUrl,
            alt: `Scene ${idx + 1}`,
            class: "scene-card__thumb rounded",
            width: THUMB_W,
            height: THUMB_H,
            draggable: false,
          }),
          h(
            "span",
            { class: "scene-card__num text-xs text-neutral-300 text-center" },
            [`Scene ${idx + 1}`],
          ),
          h("input", {
            type: "number",
            class:
              "scene-card__duration text-xs w-full min-h-[32px] bg-neutral-950 text-neutral-100 rounded px-1",
            min: 100,
            max: 60000,
            step: 100,
            value: String(scene.durationMs),
            "aria-label": `Dauer Scene ${idx + 1} (ms)`,
          }),
          h(
            "button",
            {
              type: "button",
              class:
                "scene-card__delete absolute top-0 right-0 min-h-[44px] min-w-[44px] text-red-400 text-lg",
              "aria-label": `Scene ${idx + 1} löschen`,
              disabled: state.scenes.length <= 1 ? true : null,
              "data-delete-idx": String(idx),
            },
            ["×"],
          ),
        ],
      );

      // Click → activate
      card.addEventListener("click", (e) => {
        const target = /** @type {HTMLElement} */ (e.target);
        if (target.closest(".scene-card__delete") || target.closest(".scene-card__duration")) {
          return;
        }
        setActiveSceneCommand(idx);
      });

      // Duration change → command
      const durationInput = /** @type {HTMLInputElement | null} */ (
        card.querySelector(".scene-card__duration")
      );
      if (durationInput) {
        durationInput.addEventListener("change", () => {
          const newMs = Math.max(100, Math.min(60000, Number(durationInput.value) || 2000));
          const cmd = updateSceneDurationCommand(idx, scene.durationMs, newMs);
          if (cmd) {
            execute(cmd);
          }
        });
        durationInput.addEventListener("click", (e) => e.stopPropagation());
      }

      // Delete
      const deleteBtn = /** @type {HTMLButtonElement | null} */ (
        card.querySelector(".scene-card__delete")
      );
      if (deleteBtn) {
        deleteBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          const cmd = deleteSceneCommand(idx);
          if (cmd) {
            execute(cmd);
          }
        });
      }

      // Drag-to-reorder via Pointer Events
      card.addEventListener("pointerdown", (e) => {
        if (/** @type {HTMLElement} */ (e.target).closest(".scene-card__duration")) {
          return;
        }
        if (/** @type {HTMLElement} */ (e.target).closest(".scene-card__delete")) {
          return;
        }
        draggingIdx = idx;
        card.classList.add("opacity-70");
      });
      card.addEventListener("pointerup", () => {
        if (draggingIdx !== null && draggingIdx !== idx) {
          const cmd = reorderSceneCommand(draggingIdx, idx);
          if (cmd) {
            execute(cmd);
          }
        }
        draggingIdx = null;
        card.classList.remove("opacity-70");
      });

      strip.appendChild(card);
    });

    // Plus button
    const plusBtn = h(
      "button",
      {
        type: "button",
        class:
          "scene-add min-h-[44px] min-w-[44px] bg-emerald-700 hover:bg-emerald-600 text-white rounded px-3 text-xl shrink-0",
        "aria-label": "Neue Scene",
      },
      ["+"],
    );
    plusBtn.addEventListener("click", () => {
      execute(addSceneCommand());
    });
    strip.appendChild(plusBtn);

    container.appendChild(strip);
  }

  render();
  const unsub = store.subscribe((next, prev) => {
    if (
      next.scenes !== prev.scenes ||
      next.activeSceneIndex !== prev.activeSceneIndex ||
      next.tokens !== prev.tokens
    ) {
      render();
    }
  });

  return () => {
    unsub();
    clear(container);
  };
}
